'use client';

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FilterPanel } from './FilterPanel';
import type { ActiveFilters, FilterCategories, FilterKey } from './filter-types';
import { getActiveFilterCount } from './filter-types';
import { useHideHelpScoutBeacon } from '@/hooks/useHideHelpScoutBeacon';

interface FilterSheetProps {
  open: boolean;
  onClose: () => void;
  categories: FilterCategories;
  activeFilters: ActiveFilters;
  onToggle: (key: FilterKey, handle: string) => void;
  onClearAll: () => void;
  /** Live count of artworks matching the current filters. */
  total: number;
}

/**
 * Mobile filter bottom sheet. Portalled to <body> so its fixed positioning is
 * viewport-relative regardless of the sticky toolbar's backdrop-filter stacking
 * context. Hides the HelpScout beacon while open so it never overlaps the fixed
 * "Show X artworks" action.
 */
export function FilterSheet({
  open,
  onClose,
  categories,
  activeFilters,
  onToggle,
  onClearAll,
  total,
}: FilterSheetProps) {
  const t = useTranslations('shop.filterBar');
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    setPortalTarget(document.body);
  }, []);

  useHideHelpScoutBeacon(open);

  // Prevent background scrolling, move focus inside when the sheet opens, and
  // restore the previous focus target on close.
  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = 'hidden';
    const focusFrame = window.requestAnimationFrame(() => closeButtonRef.current?.focus());

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!portalTarget) return null;

  const totalActive = getActiveFilterCount(activeFilters);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            key="sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t('filters')}
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 320 }}
            className="fixed inset-x-0 bottom-0 z-[101] flex max-h-[85vh] flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-slate-900"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-gray-200 p-4 dark:border-slate-700/70">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                {t('filters')}
                {totalActive > 0 && (
                  <span className="rounded-full bg-amber-500 px-2 py-0.5 text-xs font-bold leading-none text-white">
                    {totalActive}
                  </span>
                )}
              </h3>
              <button
                ref={closeButtonRef}
                onClick={onClose}
                aria-label={t('megaMenu.closeAriaLabel')}
                className="flex h-11 w-11 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              <FilterPanel
                categories={categories}
                activeFilters={activeFilters}
                onToggle={onToggle}
              />
            </div>

            <div
              className="flex shrink-0 gap-3 border-t border-gray-200 p-4 dark:border-slate-700/70"
              style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
            >
              {totalActive > 0 && (
                <button
                  onClick={onClearAll}
                  className="min-h-11 flex-1 rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  {t('clearAll')}
                </button>
              )}
              <button
                onClick={onClose}
                className="min-h-11 flex-1 rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
              >
                {t('showArtworks', { count: total })}
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    portalTarget,
  );
}
